import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import { LASPromptSwapIndexList } from '@adornis/digitale-helden-shared/client/theme/buildify/extensions/list/las-prompt-swap-index-list.js';
import '@adornis/digitale-helden-shared/client/theme/d-icon.js';
import { Link } from '@adornis/digitale-helden-shared/db/buildify/Link.js';
import '@adornis/popover/x-dropdown.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import '../../client/theme/d-text.js';
import { BaseLandingSearch, BaseLandingSearchItem } from '../db/BaseLandingSearch.js';

export const MarketingLandingSearchExtension: BuildifyExtension<BaseLandingSearch> = {
  class: BaseLandingSearch,
  toolbar: {
    icon: 'magnifying-glass',
    text: {
      default: 'Suche',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-landing-search
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-landing-search>
    `;
  },
  editor: ({ content, contentController, controllerBaseKeyPath, host }) => {
    return html`
      <d-flex space="md">
        ${(content.items?.length ?? 0) >= 2
          ? html`
              <d-button
                @click=${async () => {
                  if (!content) return;
                  const items = content.items ?? [];
                  const resorted = await LASPromptSwapIndexList.swapIndexes(items, item => {
                    const index = items.findIndex(i => i._id === item._id);
                    return `${index + 1}. Link`;
                  });
                  if (resorted) content.items = resorted as any[];
                  host.requestUpdate();
                }}
              >
                Reihenfolge verändern
              </d-button>
            `
          : nothing}
        <d-accordeon
          .items=${(content.items ?? []).map((item, index) => ({
            title: html` <d-text>${index + 1}. Link </d-text> `,
            text: () => html`
              <d-input
                placeholder="Name"
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'name')}
              ></d-input>
              <d-link-editor
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'link')}
              ></d-link-editor>
              <component-icon-button
                icon="trash"
                align-self="end"
                background=${host.colors.tone.warning}
                @click=${() => {
                  content.items!.splice(index, 1);
                  host.requestUpdate();
                }}
              ></component-icon-button>
            `,
          }))}
        ></d-accordeon>
        <component-icon-button
          icon="plus"
          align-self="end"
          @click=${() => {
            if (!content.items) content.items = [];
            content.items.push(new BaseLandingSearchItem({ link: new Link({}) }));
            host.requestUpdate();
          }}
        ></component-icon-button>
      </d-flex>
    `;
  },
};

@customElement('marketing-landing-search')
export class MLandingSearch extends ExtensionBoilerplate<BaseLandingSearch> {
  @state() private _activeItem: Maybe<BaseLandingSearchItem>;

  override _content(content: BaseLandingSearch) {
    const reference = createRef<XDropdown>();

    return html`
      <x-dropdown
        ${ref(reference)}
        ${css({
          alignSelf: 'center',
          padding: '8px',
          borderRadius: '4px',
          boxShadow: '4px 4px 40px rgba(0, 0, 0, 0.1)',
          opacity: '1',
          backgroundColor: 'rgba(255, 255, 255, 1)',
          boxSizing: 'border-box',
        })}
      >
        <d-flex horizontal crossaxis-center space="md">
          <d-flex
            horizontal
            crossaxis-center
            space="xs"
            ${css({ cursor: 'pointer' })}
            @click=${() => {
              reference.value?.toggle();
            }}
          >
            <d-text ${css({ color: this.colors.tone.black, width: '230px' })}>
              <i> ${this._activeItem?.name ?? 'Mich interessiert das Thema ...'} </i>
            </d-text>
            <d-icon> chevron-down </d-icon>
          </d-flex>
          <component-icon-button
            tooltip="Suchen"
            icon="magnifying-glass"
            background=${this.colors.accent}
            @click=${() => {
              if (!this._activeItem) return;
              return goTo(this._activeItem.link.link ?? '', { target: this._activeItem.link.target ?? '_blank' });
            }}
          ></component-icon-button>
        </d-flex>
        <x-flex
          slot="dropdown-content"
          ${css({
            width: '328px',
            borderRadius: '4px',
            boxShadow: '4px 4px 40px rgba(0, 0, 0, 0.1)',
            opacity: '1',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxSizing: 'border-box',
            maxHeight: '200px',
            overflow: 'auto',
          })}
        >
          ${repeat(
            content.items ?? [],
            item => item._id,
            (item, index) => {
              if (this._activeItem === item) return nothing;
              return html`
                <marketing-landing-search-dropdown-item
                  name=${item.name}
                  .link=${item.link}
                  @click=${() => {
                    this._activeItem = item;
                    reference.value?.close();
                  }}
                ></marketing-landing-search-dropdown-item>
                <d-hr></d-hr>
              `;
            },
          )}
        </x-flex>
      </x-dropdown>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          flexDirection: 'column',
        },
        '[dropdown-item]': {
          color: 'red',
          padding: '8px',
        },
      },
    ] as Styles[];
  }
}

@customElement('marketing-landing-search-dropdown-item')
export class MarketingLandingSearchDropdownItem extends ChemistryLitElement {
  @property({ type: String }) name: Maybe<string>;
  @property({ attribute: false }) link: Maybe<Link>;

  override render() {
    return html`
      <d-link color=${this.colors.tone.secondaryText}>
        <d-text> ${this.name} </d-text>
      </d-link>
    `;
  }
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          cursor: 'pointer',
          color: this.colors.tone.secondaryText,
          padding: '8px 24px !important',
        },
        ':host(:hover) d-link': {
          color: this.colors.tone.black,
        },
      },
    ] as Styles[];
  }
}
